/**
 * The comparison offer for a date leaf.
 *
 * Every date leaf reads `[Field] [comparison] [amount?] [target]` — `Claim Created / more than /
 * 90 Days before / When this rule runs`. This module owns the comparison half and the rules that
 * tie it to the other two: which phrases exist, which wire operator and direction each stands for,
 * what a phrase change does to the value, and the precision the two operand types imply.
 *
 * **A phrase is editor-only.** It maps to a fixed `(operator, dir, shape)` triple, and every
 * mapping goes through that triple rather than through the label, so changing a phrase can never
 * shift a boundary by a day. The `shape` says what the right-hand side is:
 *
 * - `point` — the target itself: a specific date (a plain date string) or another instant
 *   (`{anchor}`). Six phrases, one per comparison operator.
 * - `offset` — an amount before or after the target (`{anchor, dir, n, unit}`). Five phrases per
 *   direction.
 * - `presence` — whether the field is set at all; no right-hand side.
 *
 * ## Why "less than / at most / exactly / at least / more than"
 *
 * Every offset relation is a *ray*. A leaf holds one comparison and the difference is signed, so
 * `< n` after the target also matches every date before the target — which is deliberate, because
 * it reproduces legacy `time_of_loss` behaviour for a loss predating the policy. Two consequences
 * drive the wording:
 *
 * - A window phrase would be a lie. `was within the last 30 days` compiles to
 *   `field > evaluated_at - 30d`, which also matches every *future* date. Harmless on `Claim
 *   Created`, which cannot be future-dated, but wrong on `policy.api.expiration_date`, which
 *   normally is, and unknowable on an org's own configured date fields. So no phrase says
 *   "within".
 * - The comparative is exact at the boundary. `less than` / `exactly` / `more than` are `<` / `=` /
 *   `>` against the same `target ± n` and partition the line, so the boundary day is *named*.
 *   `at most` and `at least` are the two inclusive unions of that partition; they are offered
 *   because stored rules use them (`last_reserve_change_decision_date >= evaluated_at − 30 days`
 *   reads "at most 30 Days before When this rule runs"), and a stored leaf the menu cannot name
 *   renders as a fallback phrase instead of as the rule its author wrote.
 */

import type { FieldOption } from './buildFieldTree';
import type { DatePrecision, OperatorOption } from './operators';
import {
  DEFAULT_OFFSET_UNIT,
  EVALUATED_AT_ANCHOR,
  EXACT_OFFSET_UNITS,
  OFFSET_UNITS,
  PRECISION_DAY,
  PRECISION_EXACT,
  TRIGGERED_AT_ANCHOR,
} from './operators';
import type {
  AnchoredDateValueShape,
  DateOffsetValueShape,
  DatePointValueShape,
  FieldDefinitionDto,
  FieldType,
} from './types';
import { FIELD_TYPE } from './types';

/** What a relation's right-hand side is. See the module docblock. */
export type RelationShape = 'point' | 'offset' | 'presence';

export interface DateRelation {
  /** Editor-only, never written to `conditions_json`. Hyphenated prose so it can never be
   *  mistaken for a wire operator. */
  id: string;
  /** The phrase the author reads, and the only part of a row they ever see.
   *
   *  An offset phrase leads with its comparative (`less than … before`) rather than with `is`:
   *  ten of the eighteen rows would otherwise share a first word and differ only at the third,
   *  which costs both eye-scanning and the select's native type-ahead — MUI matches on leading
   *  text. The field name supplies the subject: *Claim Created — at most 30 Days before …*. */
  label: string;
  /** The wire operator. This, `dir` and `shape` are the whole of what a relation means — the
   *  label is presentation. */
  operator: string;
  shape: RelationShape;
  /** Which side of the target the offset falls on. Offset rows only. */
  dir?: string;
}

// One row per line: the table is meant to be read as a table, and each row is the whole of what a
// phrase means. `test_be_fe_operator_contract.py` parses these literals, so every row stays a flat
// literal inside this array.
// prettier-ignore
export const DATE_RELATIONS: DateRelation[] = [
  { id: 'is', label: 'is', operator: '=', shape: 'point' },
  { id: 'is-not', label: 'is not', operator: '!=', shape: 'point' },
  { id: 'is-before', label: 'is before', operator: '<', shape: 'point' },
  { id: 'is-on-or-before', label: 'is on or before', operator: '<=', shape: 'point' },
  { id: 'is-after', label: 'is after', operator: '>', shape: 'point' },
  { id: 'is-on-or-after', label: 'is on or after', operator: '>=', shape: 'point' },

  { id: 'less-than-before', label: 'less than … before', operator: '>', shape: 'offset', dir: 'before' },
  { id: 'at-most-before', label: 'at most … before', operator: '>=', shape: 'offset', dir: 'before' },
  { id: 'exactly-before', label: 'exactly … before', operator: '=', shape: 'offset', dir: 'before' },
  { id: 'at-least-before', label: 'at least … before', operator: '<=', shape: 'offset', dir: 'before' },
  { id: 'more-than-before', label: 'more than … before', operator: '<', shape: 'offset', dir: 'before' },

  { id: 'less-than-after', label: 'less than … after', operator: '<', shape: 'offset', dir: 'after' },
  { id: 'at-most-after', label: 'at most … after', operator: '<=', shape: 'offset', dir: 'after' },
  { id: 'exactly-after', label: 'exactly … after', operator: '=', shape: 'offset', dir: 'after' },
  { id: 'at-least-after', label: 'at least … after', operator: '>=', shape: 'offset', dir: 'after' },
  { id: 'more-than-after', label: 'more than … after', operator: '>', shape: 'offset', dir: 'after' },

  { id: 'is-empty', label: 'is empty', operator: 'null', shape: 'presence' },
  { id: 'is-not-empty', label: 'is not empty', operator: 'notNull', shape: 'presence' },
];

/** The comparisons an exact (to-the-minute) comparison refuses: an instant has no width, so "is",
 *  "is not" and "exactly …" would each hang on a single instant that practically never recurs.
 *  The menu hides these on an exact leaf and the BE rejects them. Must match
 *  `rules_engine/cel/operator_map.py::EXACT_REFUSED_OPERATORS`. */
export const EXACT_REFUSED_OPERATORS: ReadonlyArray<string> = ['=', '!='];

export const relationById = (id: string): DateRelation | undefined => DATE_RELATIONS.find((r) => r.id === id);

/** An anchored value is any object carrying an `anchor` — a point or an offset — matching the
 *  BE's `operator_map.is_anchored_value`. A leaf that has never been touched arrives with RQB's
 *  scalar placeholder instead, and a specific date is a plain string. */
export const isAnchoredValue = (value: unknown): value is AnchoredDateValueShape =>
  typeof value === 'object' && value !== null && 'anchor' in value;

/** The keys that make an anchored value an offset. Any one of them does — the BE's
 *  `operator_map.is_offset_shape` — and the BE then requires all three, so a value carrying some
 *  but not all of them is a malformed offset rather than a point with stray keys. */
const OFFSET_SHAPE_KEYS: ReadonlyArray<keyof DateOffsetValueShape> = ['dir', 'n', 'unit'];

export const isOffsetShape = (value: unknown): value is DateOffsetValueShape =>
  isAnchoredValue(value) && OFFSET_SHAPE_KEYS.some((key) => key in value);

/** The anchors that are not catalog paths: instants the evaluator supplies at run time. */
const RESERVED_ANCHORS: ReadonlyArray<string> = [EVALUATED_AT_ANCHOR, TRIGGERED_AT_ANCHOR];

export const isReservedAnchor = (anchor: unknown): boolean =>
  typeof anchor === 'string' && RESERVED_ANCHORS.includes(anchor);

/**
 * The relation a saved leaf is currently expressing, or `null` when the offer cannot express it.
 *
 * Every stored shape resolves: a presence operator by its operator, a specific date or a point by
 * its operator (all six are offered), an offset by its `(operator, dir)` pair (all five of each
 * direction are offered). What is left is an offset the offer has no phrase for — `!=`, which a
 * hand-edited import can write, or a direction the BE does not know — and snapping such a leaf to
 * the nearest phrase would change its meaning. The caller renders `describeUnofferedRelation`.
 */
export const relationForLeaf = (operator: string, value: unknown): DateRelation | null => {
  const presence = DATE_RELATIONS.find((r) => r.shape === 'presence' && r.operator === operator);
  if (presence) return presence;
  if (isOffsetShape(value)) {
    return DATE_RELATIONS.find((r) => r.shape === 'offset' && r.operator === operator && r.dir === value.dir) ?? null;
  }
  return DATE_RELATIONS.find((r) => r.shape === 'point' && r.operator === operator) ?? null;
};

/**
 * An honest phrase for a leaf no offered relation matches.
 *
 * Built from the point phrases plus the stored direction, so the row reads as the comparison it
 * actually is — `is not … before` — rather than as whichever phrase sits nearest it in the list.
 * An offset stored without a direction keeps the ellipsis and names none.
 */
export const describeUnofferedRelation = (operator: string, value: unknown): string => {
  const phrase = DATE_RELATIONS.find((r) => r.shape !== 'offset' && r.operator === operator)?.label ?? operator;
  if (!isOffsetShape(value)) return phrase;
  return typeof value.dir === 'string' ? `${phrase} … ${value.dir}` : `${phrase} …`;
};

/** A point value on `anchor`, carrying `precision` only when the value it replaces carried one —
 *  so a leaf stored without the marker keeps meaning whole-day. */
const pointValue = (anchor: string, precision: DatePrecision | undefined): DatePointValueShape =>
  precision === undefined ? { anchor } : { anchor, precision };

/**
 * The value a leaf should carry once `relation` is chosen, given what it carries now.
 *
 * Everything the author chose that the new relation still has a use for is preserved — the
 * target, the count, the unit and the precision marker (including its absence) — so re-phrasing a
 * row never silently re-authors it. What the new relation has no use for is dropped:
 *
 * - An emptiness check emits `''`, which is what RQB seeds a fresh date leaf with. Leaving an
 *   anchored value behind would be worse than untidy: `_compile_leaf` dispatches on the value
 *   *shape* before it reads the operator, so a stale anchor under `null` 400s a row that looks
 *   complete on screen.
 * - A point phrase drops the amount (`dir` / `n` / `unit`) but keeps the target: a specific date
 *   stays the date the author typed, an instant stays that instant. An offset whose target was
 *   never picked becomes the fresh-row default, a specific date.
 * - An offset phrase keeps an anchored target and otherwise leaves it unpicked, rather than
 *   seeding one — which would author a comparison the user never chose. The empty target has a
 *   message in `validateConditionsTree`.
 */
export const applyRelation = (relation: DateRelation, currentValue: unknown): unknown => {
  if (relation.shape === 'presence') {
    return '';
  }

  if (relation.shape === 'point') {
    if (!isAnchoredValue(currentValue)) {
      return currentValue ?? '';
    }
    return currentValue.anchor ? pointValue(currentValue.anchor, currentValue.precision) : '';
  }

  const anchored = isAnchoredValue(currentValue) ? currentValue : null;
  const offset = isOffsetShape(currentValue) ? currentValue : null;
  const next: DateOffsetValueShape = {
    anchor: anchored?.anchor ?? '',
    dir: relation.dir!,
    n: offset?.n ?? '',
    unit: offset?.unit ?? DEFAULT_OFFSET_UNIT,
  };
  return anchored?.precision === undefined ? next : { ...next, precision: anchored.precision };
};

/* --- the target ------------------------------------------------------------- */

/** Editor-only value the target picker holds while the right-hand side is a specific date. It
 *  never reaches the wire: choosing it writes the plain date string the value has always been. */
export const SPECIFIC_DATE_TARGET = '__specific_date__';

/** How the target picker names its non-catalog entries. The validator names them the same way,
 *  so an error points at the words on screen. */
export const TARGET_LABEL = {
  specificDate: 'A specific date…',
  eventOccurred: 'When the event occurred',
  ruleRuns: 'When this rule runs',
} as const;

/** The entries pinned above the catalog's date fields in the target picker. A specific date is a
 *  point target only — an amount before a typed date is a date the author can type directly —
 *  and the event time exists only on a rule that has a triggering event. */
export const targetLeadingOptions = ({
  offersSpecificDate,
  offersEventTime,
}: {
  offersSpecificDate: boolean;
  offersEventTime: boolean;
}): FieldOption[] => [
  ...(offersSpecificDate ? [{ name: SPECIFIC_DATE_TARGET, label: TARGET_LABEL.specificDate }] : []),
  ...(offersEventTime ? [{ name: TRIGGERED_AT_ANCHOR, label: TARGET_LABEL.eventOccurred }] : []),
  { name: EVALUATED_AT_ANCHOR, label: TARGET_LABEL.ruleRuns },
];

/** The field type of what a target resolves to: the reserved anchors are instants, so datetime; a
 *  catalog anchor has the type the registry declares; `undefined` for an unpicked target or a path
 *  the registry does not serve. */
export const targetFieldType = (
  anchor: unknown,
  fieldsByPath: Readonly<Record<string, Pick<FieldDefinitionDto, 'field_type'> | undefined>>
): FieldType | undefined => {
  if (typeof anchor !== 'string' || !anchor) return undefined;
  if (isReservedAnchor(anchor)) return FIELD_TYPE.Datetime;
  return fieldsByPath[anchor]?.field_type;
};

/** The precision a comparison between two operand types carries — never a toggle. Exact when both
 *  sides are date-and-time values (a datetime field against a datetime field, When the event
 *  occurred or When this rule runs); whole-day as soon as either side is a date, so "is When this
 *  rule runs" on a date field keeps meaning "is today". */
export const derivePrecision = (fieldType: FieldType, targetType: FieldType | undefined): DatePrecision =>
  fieldType === FIELD_TYPE.Datetime && targetType === FIELD_TYPE.Datetime ? PRECISION_EXACT : PRECISION_DAY;

/** The precision a stored value compiles with: its marker, or whole-day when it carries none —
 *  which is every leaf written before the marker existed, and every specific date. */
export const effectivePrecision = (value: unknown): DatePrecision =>
  isAnchoredValue(value) && value.precision === PRECISION_EXACT ? PRECISION_EXACT : PRECISION_DAY;

/** The comparisons the menu offers a leaf currently holding `value`: all of them, less the ones an
 *  exact comparison refuses when the leaf is exact. */
export const offeredRelations = (value: unknown): DateRelation[] =>
  effectivePrecision(value) === PRECISION_EXACT
    ? DATE_RELATIONS.filter((relation) => !EXACT_REFUSED_OPERATORS.includes(relation.operator))
    : DATE_RELATIONS;

/** The units the amount can be counted in, at the precision the comparison derives. */
export const offeredUnits = (precision: DatePrecision): OperatorOption[] =>
  precision === PRECISION_EXACT ? EXACT_OFFSET_UNITS : OFFSET_UNITS;

// Derived as the difference of the two lists rather than named, so a unit added to either lands on
// the right side of this line by construction. Today it is Hours alone.
const EXACT_ONLY_UNITS: ReadonlyArray<string> = EXACT_OFFSET_UNITS.filter(
  (unit) => !OFFSET_UNITS.some((wholeDay) => wholeDay.name === unit.name)
).map((unit) => unit.name);

/** Whether only an exact comparison can count in `unit` — whole-day precision truncates to days,
 *  so an hour count there would silently mean days. */
export const isExactOnlyUnit = (unit: unknown): boolean => typeof unit === 'string' && EXACT_ONLY_UNITS.includes(unit);

/**
 * The value a leaf carries once `target` is picked in the target picker.
 *
 * `precision` is the one derived for the new pair of operand types, and it is stamped here and
 * nowhere else in the target's lifecycle: a count, unit or comparison edit leaves the marker —
 * including its absence on a stored leaf — exactly as it is (see `applyRelation`). The specific
 * date entry writes the plain date string back, keeping one already typed.
 */
export const applyTarget = (currentValue: unknown, target: string, precision: DatePrecision): unknown => {
  if (target === SPECIFIC_DATE_TARGET) {
    return isAnchoredValue(currentValue) ? '' : (currentValue ?? '');
  }
  if (isOffsetShape(currentValue)) {
    return { ...currentValue, anchor: target, precision };
  }
  return { anchor: target, precision } satisfies DatePointValueShape;
};

/** The value an offset carries once `unit` is picked. An exact-only unit makes the comparison
 *  exact — the one case besides a target change where the marker is written, because Hours is
 *  meaningless at whole-day precision and is only offered where the operands derive exact. */
export const applyUnit = (offset: DateOffsetValueShape, unit: string): DateOffsetValueShape =>
  isExactOnlyUnit(unit) ? { ...offset, unit, precision: PRECISION_EXACT } : { ...offset, unit };

/**
 * Whether a field can carry an anchored value at all, and so whether the operator cell offers the
 * comparison menu and the value cell offers a target.
 *
 * `time` is excluded because every temporal CEL function measures dates, so "30 days before 09:00"
 * has no meaning the emission could carry — and `time` shares the same operator list as `date`, so
 * this check is the only thing keeping an anchor off it. Type-guarded fields (an involved-party
 * variant field, a communication field copied into a channel group) are excluded because their
 * catalog path is synthetic: the compiler rejects an anchored value there rather than emit against
 * a path with no runtime data.
 */
export const fieldOffersOffset = (fieldType: FieldType, isVariantField: boolean): boolean =>
  (fieldType === FIELD_TYPE.Date || fieldType === FIELD_TYPE.Datetime) && !isVariantField;
